import { c as _c } from "react/compiler-runtime";
import type { UseComboboxProps, UseComboboxReturnValue, UseComboboxState, UseComboboxStateChangeTypes, UseMultipleSelectionReturnValue } from 'downshift';
import { useCombobox, useMultipleSelection } from 'downshift';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { BaseSelectorProps, DisabledProps, MultiSelectorProps } from '../common/types';
interface CommonSelectorOptions<T> extends Pick<UseComboboxProps<T>, 'items' | 'itemToString' | 'onStateChange' | 'stateReducer'>, Pick<BaseSelectorProps<T>, 'getDisabledProps' | 'onSearch' | 'getItemKey'> {
  inputRef: RefObject<HTMLElement | null>;
}
interface UseBaseSelectorResult<T> extends Pick<UseComboboxReturnValue<T>, 'getComboboxProps' | 'getMenuProps' | 'getItemProps' | 'getInputProps' | 'getToggleButtonProps' | 'isOpen' | 'highlightedIndex' | 'openMenu' | 'closeMenu'> {
  clear: () => void;
  inputValue: string;
}
interface UseSelectorOptions<T> extends CommonSelectorOptions<T>, Pick<UseComboboxProps<T>, 'onSelectedItemChange'> {
  selectedItem: T | null;
}
interface UseSelectorResult<T> extends UseBaseSelectorResult<T> {
  selectedItem: T | null;
  selectedItemKey: string | null;
}
export function useSelector(t0) {
  const $ = _c(44);
  let getDisabledProps;
  let getItemKey;
  let inputRef;
  let itemToString;
  let items;
  let onSearch;
  let rest;
  let selectedItem;
  if ($[0] !== t0) {
    ({
      items,
      itemToString,
      selectedItem,
      onSearch,
      getDisabledProps,
      getItemKey,
      inputRef,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = getDisabledProps;
    $[2] = getItemKey;
    $[3] = inputRef;
    $[4] = itemToString;
    $[5] = items;
    $[6] = onSearch;
    $[7] = rest;
    $[8] = selectedItem;
  } else {
    getDisabledProps = $[1];
    getItemKey = $[2];
    inputRef = $[3];
    itemToString = $[4];
    items = $[5];
    onSearch = $[6];
    rest = $[7];
    selectedItem = $[8];
  }
  let t1;
  if ($[9] !== getItemKey || $[10] !== selectedItem) {
    t1 = selectedItem ? getItemKey(selectedItem) : null;
    $[9] = getItemKey;
    $[10] = selectedItem;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const selectedItemKey = t1;
  const prevSelectedItemRef = useRef(selectedItem);
  let t2;
  if ($[12] !== onSearch) {
    t2 = changes => onSearch?.(changes.inputValue ?? "");
    $[12] = onSearch;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== getDisabledProps) {
    t3 = (state, t4) => {
      const {
        type,
        changes: changes_0
      } = t4;
      let disabled;
      if (defaultReducer[type as DefaultReducerActions]) {
        return defaultReducer[type as DefaultReducerActions]?.(state, {
          type,
          changes: changes_0
        }) as Partial<UseComboboxState<T>>;
      }
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          {}
        case useCombobox.stateChangeTypes.ItemClick:
          {
            disabled = changes_0.selectedItem ? getDisabledProps?.(changes_0.selectedItem, {
              index: state.highlightedIndex
            }) : undefined;
            if (disabled?.isDisabled) {
              return state;
            }
            return {
              ...changes_0,
              inputValue: ""
            };
          }
        default:
          {
            return changes_0;
          }
      }
    };
    $[14] = getDisabledProps;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== inputRef) {
    t4 = changes_1 => {
      if (changes_1.isOpen) {
        inputRef.current?.focus();
      }
    };
    $[16] = inputRef;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== itemToString || $[19] !== items || $[20] !== rest || $[21] !== selectedItem || $[22] !== t2 || $[23] !== t3 || $[24] !== t4) {
    t5 = {
      items,
      itemToString,
      selectedItem,
      onInputValueChange: t2,
      stateReducer: t3,
      onIsOpenChange: t4,
      ...rest
    };
    $[18] = itemToString;
    $[19] = items;
    $[20] = rest;
    $[21] = selectedItem;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const {
    getComboboxProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    getToggleButtonProps,
    isOpen,
    highlightedIndex,
    openMenu,
    closeMenu,
    inputValue,
    reset
  } = useCombobox(t5);
  let t6;
  let t7;
  if ($[26] !== reset || $[27] !== selectedItem) {
    t6 = () => {
      const prevSelectedItem = prevSelectedItemRef.current;
      if (prevSelectedItem && !selectedItem) {
        reset();
      }
      prevSelectedItemRef.current = selectedItem;
    };
    t7 = [selectedItem, reset];
    $[26] = reset;
    $[27] = selectedItem;
    $[28] = t6;
    $[29] = t7;
  } else {
    t6 = $[28];
    t7 = $[29];
  }
  useEffect(t6, t7);
  let t8;
  if ($[30] !== closeMenu || $[31] !== getComboboxProps || $[32] !== getInputProps || $[33] !== getItemProps || $[34] !== getMenuProps || $[35] !== getToggleButtonProps || $[36] !== highlightedIndex || $[37] !== inputValue || $[38] !== isOpen || $[39] !== openMenu || $[40] !== reset || $[41] !== selectedItem || $[42] !== selectedItemKey) {
    t8 = {
      getComboboxProps,
      getInputProps,
      getItemProps,
      getMenuProps,
      getToggleButtonProps,
      highlightedIndex,
      isOpen,
      closeMenu,
      openMenu,
      inputValue,
      clear: reset,
      selectedItem,
      selectedItemKey
    };
    $[30] = closeMenu;
    $[31] = getComboboxProps;
    $[32] = getInputProps;
    $[33] = getItemProps;
    $[34] = getMenuProps;
    $[35] = getToggleButtonProps;
    $[36] = highlightedIndex;
    $[37] = inputValue;
    $[38] = isOpen;
    $[39] = openMenu;
    $[40] = reset;
    $[41] = selectedItem;
    $[42] = selectedItemKey;
    $[43] = t8;
  } else {
    t8 = $[43];
  }
  return t8;
}
interface UseMultipleSelectorOptions<T> extends CommonSelectorOptions<T>, Pick<BaseSelectorProps<T>, 'getItemKey' | 'onSearch'>, Pick<MultiSelectorProps<T>, 'onSelect' | 'selectedItems'> {}
interface UseMultiSelectorResult<T> extends UseBaseSelectorResult<T>, Pick<UseMultipleSelectionReturnValue<T>, 'getDropdownProps' | 'removeSelectedItem'> {
  selectedItemKeys: string[];
  selectAll: () => void;
  isAllSelected: boolean;
}
export function useMultiSelector(t0) {
  const $ = _c(50);
  const {
    inputRef,
    items,
    itemToString,
    selectedItems: selectedItemsProp,
    getDisabledProps,
    onSearch,
    getItemKey,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect) {
    t1 = changes => {
      onSelect(changes.selectedItems ?? []);
    };
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== itemToString || $[3] !== selectedItemsProp || $[4] !== t1) {
    t2 = {
      selectedItems: selectedItemsProp,
      onSelectedItemsChange: t1,
      itemToString
    };
    $[2] = itemToString;
    $[3] = selectedItemsProp;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    getDropdownProps,
    removeSelectedItem,
    addSelectedItem,
    selectedItems
  } = useMultipleSelection(t2);
  let t3;
  if ($[6] !== getItemKey || $[7] !== selectedItems) {
    t3 = selectedItems.map(getItemKey);
    $[6] = getItemKey;
    $[7] = selectedItems;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const selectedItemKeys = t3;
  const availableItemKeys = items.filter((item, index) => !getDisabledProps?.(item, {
    index
  })?.isDisabled).map(getItemKey);
  let t4;
  if ($[9] !== addSelectedItem || $[10] !== getItemKey || $[11] !== removeSelectedItem || $[12] !== selectedItemKeys || $[13] !== selectedItemsProp) {
    t4 = t5 => {
      const {
        selectedItem,
        type
      } = t5;
      bb16: switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          {}
        case useCombobox.stateChangeTypes.ItemClick:
          {
            if (!selectedItem) {
              return;
            }
            if (selectedItemKeys.includes(getItemKey(selectedItem))) {
              const itemToRemove = selectedItemsProp.find(item_0 => getItemKey(item_0) === getItemKey(selectedItem));
              removeSelectedItem(itemToRemove as T);
            } else {
              addSelectedItem(selectedItem);
            }
            break bb16;
          }
        default:
          {}
      }
    };
    $[9] = addSelectedItem;
    $[10] = getItemKey;
    $[11] = removeSelectedItem;
    $[12] = selectedItemKeys;
    $[13] = selectedItemsProp;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== getDisabledProps) {
    t5 = (state, t6) => {
      const {
        type: type_0,
        changes: changes_0
      } = t6;
      let disabled;
      if (defaultReducer[type_0 as DefaultReducerActions]) {
        return defaultReducer[type_0 as DefaultReducerActions]?.(state, {
          type: type_0,
          changes: changes_0
        }) as Partial<UseComboboxState<T>>;
      }
      switch (type_0) {
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          {
            return {
              ...changes_0,
              inputValue: state.inputValue
            };
          }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          {}
        case useCombobox.stateChangeTypes.ItemClick:
          {
            disabled = changes_0.selectedItem ? getDisabledProps?.(changes_0.selectedItem, {
              index: state.highlightedIndex
            }) : undefined;
            if (disabled?.isDisabled) {
              return state;
            }
            return {
              ...changes_0,
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
              inputValue: state.inputValue
            };
          }
        default:
          {
            return changes_0;
          }
      }
    };
    $[15] = getDisabledProps;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== getItemKey || $[18] !== inputRef || $[19] !== itemToString || $[20] !== items || $[21] !== onSearch || $[22] !== t4 || $[23] !== t5) {
    t6 = {
      items,
      itemToString,
      selectedItem: null,
      inputRef,
      onSearch,
      getItemKey,
      onStateChange: t4,
      stateReducer: t5
    };
    $[17] = getItemKey;
    $[18] = inputRef;
    $[19] = itemToString;
    $[20] = items;
    $[21] = onSearch;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const {
    getComboboxProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    getToggleButtonProps,
    inputValue,
    isOpen,
    highlightedIndex,
    openMenu,
    closeMenu
  } = useSelector(t6);
  let t7;
  if ($[25] !== getDisabledProps || $[26] !== items || $[27] !== onSelect) {
    t7 = function handleSelectAll() {
      const availableItems = items.filter((item_1, index_0) => !getDisabledProps?.(item_1, {
        index: index_0
      })?.isDisabled);
      onSelect(availableItems);
    };
    $[25] = getDisabledProps;
    $[26] = items;
    $[27] = onSelect;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleSelectAll = t7;
  const t8 = selectedItemKeys.length === availableItemKeys.length;
  let t9;
  if ($[29] !== getDisabledProps || $[30] !== items || $[31] !== onSelect) {
    t9 = () => {
      const unremovableItems = items.filter((item_2, index_1) => getDisabledProps?.(item_2, {
        index: index_1
      })?.isRemovable === false);
      onSelect(unremovableItems);
    };
    $[29] = getDisabledProps;
    $[30] = items;
    $[31] = onSelect;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== closeMenu || $[34] !== getComboboxProps || $[35] !== getDropdownProps || $[36] !== getInputProps || $[37] !== getItemProps || $[38] !== getMenuProps || $[39] !== getToggleButtonProps || $[40] !== handleSelectAll || $[41] !== highlightedIndex || $[42] !== inputValue || $[43] !== isOpen || $[44] !== openMenu || $[45] !== removeSelectedItem || $[46] !== selectedItemKeys || $[47] !== t8 || $[48] !== t9) {
    t10 = {
      getComboboxProps,
      getDropdownProps,
      getInputProps,
      getItemProps,
      getMenuProps,
      getToggleButtonProps,
      highlightedIndex,
      inputValue,
      isOpen,
      closeMenu,
      openMenu,
      isAllSelected: t8,
      clear: t9,
      selectAll: handleSelectAll,
      selectedItemKeys,
      removeSelectedItem
    };
    $[33] = closeMenu;
    $[34] = getComboboxProps;
    $[35] = getDropdownProps;
    $[36] = getInputProps;
    $[37] = getItemProps;
    $[38] = getMenuProps;
    $[39] = getToggleButtonProps;
    $[40] = handleSelectAll;
    $[41] = highlightedIndex;
    $[42] = inputValue;
    $[43] = isOpen;
    $[44] = openMenu;
    $[45] = removeSelectedItem;
    $[46] = selectedItemKeys;
    $[47] = t8;
    $[48] = t9;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  return t10;
}
type SelectorStateReducer<T> = UseComboboxProps<T>['stateReducer'];
type DefaultReducerActions = UseComboboxStateChangeTypes.ToggleButtonClick | UseComboboxStateChangeTypes.FunctionOpenMenu | UseComboboxStateChangeTypes.FunctionReset;
const defaultReducer: Record<DefaultReducerActions, SelectorStateReducer<unknown>> = {
  [useCombobox.stateChangeTypes.ToggleButtonClick]: (_state, {
    changes
  }) => {
    return {
      ...changes,
      inputValue: ''
    };
  },
  [useCombobox.stateChangeTypes.FunctionOpenMenu]: (_state, {
    changes
  }) => {
    return {
      ...changes,
      inputValue: ''
    };
  },
  [useCombobox.stateChangeTypes.FunctionReset]: (_state, {
    changes
  }) => {
    return {
      ...changes,
      isOpen: false
    };
  }
};